// emitCss.ts
import { buildRoleTokens } from './roleTokens';
import { buildComponentTokens } from './componentTokens';
import { contrastRatio } from './contrast';
import type { SeedConfig, TokenMap } from './types';

const INTENT = ['brand', 'neutral', 'danger', 'success', 'warning', 'info'] as const;

export interface ContrastIssue {
  role: string;
  ratio: number;
}

export function validateContrast(roleTokens: TokenMap, min = 4.5): ContrastIssue[] {
  const issues: ContrastIssue[] = [];
  for (const role of INTENT) {
    const bg = roleTokens[`${role}-bg`];
    const fg = roleTokens[`${role}-fg`];
    if (!bg || !fg) continue;
    const ratio = contrastRatio(fg, bg);
    if (ratio < min) issues.push({ role, ratio: Math.round(ratio * 100) / 100 });
  }
  return issues;
}

/** Light roles on :root and on any subtree that forces light (e.g. a wallboard). */
export const NXT_LIGHT_SELECTOR = ":root, [data-theme='light']";
/** Dark roles on a dark document root and on any subtree that forces dark. */
export const NXT_DARK_SELECTOR = ":root[data-theme='dark'], [data-theme='dark']";

function renderBlock(selector: string, tokens: TokenMap): string {
  const lines = Object.entries(tokens)
    .map(([k, v]) => `    --nxt-${k}: ${v};`)
    .join('\n');
  return `  ${selector} {\n${lines}\n  }\n`;
}

function assertContrast(label: string, roles: TokenMap): void {
  const issues = validateContrast(roles);
  if (issues.length) {
    const detail = issues.map((i) => `${i.role} (${i.ratio}:1)`).join(', ');
    throw new Error(`nx-tokens: ${label} on-fill contrast below AA 4.5 for: ${detail}`);
  }
}

/** `*-size` and `heading-size-N` keys — the tokens that carry a font/size step. */
export function isComponentSizeKey(key: string): boolean {
  return key.endsWith('-size') || /^heading-size-\d+$/.test(key);
}

export function emitTokensCss(config: SeedConfig): string {
  // Light/dark is the only axis in the generated sheet. Brand schemes are
  // injected at runtime (unlayered) from the appearance catalog — see
  // emitAppearanceSchemeCss / emitRuntimeBrandCss. Do not emit [data-brand]
  // blocks; Connect never sets that attribute.
  const componentTokens = buildComponentTokens();
  const blocks: string[] = [];

  const baseLight = buildRoleTokens(config);
  const baseDark = buildRoleTokens({ ...config, mode: 'dark' });
  assertContrast('light', baseLight);
  assertContrast('dark', baseDark);
  // Component SIZE tokens reference the --nxt-font-size-* steps, which are
  // re-declared per scale scope (`:root, [data-nx-scope]` in base.css). A var()
  // inside a custom property is substituted where the property is declared, so
  // these must be declared on the same selector list or a scope would inherit
  // the root's frozen value.
  const sizeTokens: TokenMap = {};
  const otherTokens: TokenMap = {};
  for (const [key, value] of Object.entries(componentTokens)) {
    (isComponentSizeKey(key) ? sizeTokens : otherTokens)[key] = value;
  }
  blocks.push(renderBlock(NXT_LIGHT_SELECTOR, { ...baseLight, ...otherTokens }));
  blocks.push(renderBlock(':root, [data-nx-scope]', sizeTokens));
  blocks.push(renderBlock(NXT_DARK_SELECTOR, baseDark));

  return (
    `/* AUTO-GENERATED by src/generator/generate.ts — do not edit by hand. */\n` +
    `@layer nxt.tokens {\n${blocks.join('\n')}}\n`
  );
}
